/*
Purpose: Common reusable modal for marking a payment as "Paid", driven by Formik.
         - OFFLINE (pp_pay_method == 2): existing system, unchanged. Wallet toggle
           (if balance available) + Payment Mode/Transaction ID shown whenever
           there's a remaining balance OR the wallet isn't being used at all.
         - ONLINE (pp_pay_method == 1): same wallet toggle (shown only if the
           student has a wallet balance). Payment Mode + Transfer ID are shown
           ONLY when the wallet is enabled AND still leaves a remaining balance
           after deduction — i.e. the wallet covers what it can, and the rest is
           recorded against the online invoice via a normal payment mode. If the
           wallet fully covers the amount, no mode/id is needed at all.
           The dropdown itself reuses the same offline payment-mode list — there
           is no separate "Wallet Transfer" mode. On submit we additionally flag
           the record so the backend knows the wallet portion was credited to the
           student's wallet and immediately applied to this online invoice.
Developer: BY KTH
Date: 01/09/2026 (updated)
Project: TCMS
*/

import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Select from "react-select";
import Moment from "moment";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import { security_key, BASE_URL } from "../global";

/**
 * Shared "Set as Paid" modal.
 *
 * @param {boolean}  show                 - controls modal visibility
 * @param {function} onHide                - called to close the modal (Cancel / X / after success)
 * @param {object}   offlinePaidData       - the ps_ row being paid. Needs: ps_amount, ps_due_amount,
 *                                           is_part_payment, pp_pay_method, sest_name, pp_name,
 *                                           ps_frq_name, ps_from_date, ps_end_date, pk_ps_key,
 *                                           fk_student_key, ...
 * @param {function} setOfflinePaidData   - setter so the parent can clear its row state on close/success
 * @param {object}   studentData           - { st_first_name, st_surname }
 * @param {array}    offlineModesOptions  - [{ value, label }] payment-mode react-select options,
 *                                           used for BOTH offline and online records.
 * @param {function} onSuccess             - called after a successful submit, e.g. () => fetchData()
 */
function OfflineSetAsPaidPopup({
  show,
  onHide,
  offlinePaidData,
  setOfflinePaidData,
  studentData,
  offlineModesOptions = [],
  onSuccess,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [studentWalletBalance, setStudentWalletBalance] = useState(0);

  // pp_pay_method: 1 = Online, 2 = Offline (same convention as the report/grid columns).
  const isOnlinePayment = Number(offlinePaidData?.pp_pay_method) === 1;

  // Mode id 1 = "fully paid from wallet, no separate mode/id needed".
  const isWalletOnlyMode = (modeId) => Number(modeId) === 1;

  const validationSchema = yup.object().shape({
    is_deducted_from_wallet: yup.number(),
    ps_due_amount_new: yup.number(),
    pk_cpm_key_e: yup.string().when(
      ["is_deducted_from_wallet", "ps_due_amount_new"],
      ([is_deducted_from_wallet, ps_due_amount_new]) => {
        // OFFLINE: required whenever the wallet isn't (fully) covering the amount - unchanged.
        // ONLINE: only required in the specific "wallet enabled + remaining balance" case,
        // since that's the only scenario where the Payment Mode field is even shown.
        const required = isOnlinePayment
          ? Number(is_deducted_from_wallet) === 0
          : Number(is_deducted_from_wallet) === 0;
        return required ? yup.string().required("Payment Mode is required.") : yup.string().notRequired();
      }
    ),
    eps_transaction_id: yup.string().when(
      ["is_deducted_from_wallet", "ps_due_amount_new", "pk_cpm_key_e"],
      ([is_deducted_from_wallet, ps_due_amount_new, pk_cpm_key_e]) => {
        const required = Number(is_deducted_from_wallet) === 0 || pk_cpm_key_e;
        return required ? yup.string().required("Transaction ID is required.") : yup.string().notRequired();
      }
    ),
  });

  const initialValues = {
    eps_transaction_id: "",
    pk_cpm_key_e: "",
    is_deducted_from_wallet: 0,
    is_part_payment_new: 0,
    ps_due_amount_new: 0,
    fk_cent_key: sessionStorage.getItem("key"),
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const response = await axios.post(BASE_URL + "API/api_index.php", {
          security_key: security_key,
          mode: "addOfflineOnlinePaymentByStudentCenter",
          ...values,
          // Online + wallet used: tells the backend the wallet portion should be
          // credited to the student's wallet and immediately applied to this
          // online invoice, rather than treated as a plain offline entry.
          // TODO: confirm this is the field/flag your API actually expects.
          is_wallet_transfer: isOnlinePayment && Number(values.is_deducted_from_wallet) === 1 ? 1 : 0,
          ...offlinePaidData,
        });

        if (response.data.success === true && response.data.data === 1) {
          toast.success("Success! The amount has been paid successfully.", {
            autoClose: 3000,
          });
          setOfflinePaidData(null);
          onHide();
          onSuccess && onSuccess();
        }else if (response.data.data === -1) {
            toast.error("Insufficient wallet balance. Please try again later.", {
                autoClose: 3000,
            });
        } else {
          toast.error("Failure! Unable to pay the amount. Try again later", {
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.error("Error submitting offline payment:", error);
        toast.error(error.message || "Failure! Unable to pay the amount. Try again later", {
          autoClose: 3000,
        });
      } finally {
        setIsSubmitting(false);
        setOfflinePaidData(null);
        onHide();
        onSuccess && onSuccess();
      }
    },
  });

  const generateFiveDigitNumber = () =>
    Math.floor(10000 + Math.random() * 90000).toString();

  const fetchStudentWalletBalance = async () => {
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "getStudentWalletBalanceData",
        fk_cent_key: sessionStorage.getItem("key"),
        fk_student_key: offlinePaidData?.fk_student_key || studentData?.value,
      });

      if (response.data.success === true) {
        setStudentWalletBalance(Number(response.data.data || 0));
      }
    } catch (error) {
      console.error("Error fetching student wallet balance:", error);
    }
  };

  // Auto compute the wallet / balance split whenever the wallet toggle or amounts
  // change. Same calculation for ONLINE and OFFLINE — only the visibility of the
  // Payment Mode section (below, in the JSX) differs between the two.
  useEffect(() => {
    const isWalletDeducted = Number(formik.values.is_deducted_from_wallet) === 1;

    const psAmount =
      Number(offlinePaidData?.is_part_payment) === 1
        ? Number(offlinePaidData?.ps_due_amount || 0)
        : Number(offlinePaidData?.ps_amount || 0);
    const walletBalance = Number(studentWalletBalance || 0);

    if (isWalletDeducted) {
      const dueAmount = (psAmount - walletBalance).toFixed(2);

      if (dueAmount > 0) {
        formik.setFieldValue("is_part_payment_new", 1);
        formik.setFieldValue("ps_due_amount_new", dueAmount);
      } else {
        formik.setFieldValue("is_part_payment_new", 0);
        formik.setFieldValue("ps_due_amount_new", 0);
        formik.setFieldValue("pk_cpm_key_e", 1); // fully paid from wallet, no separate mode needed
        formik.setFieldValue("eps_transaction_id", generateFiveDigitNumber());
      }
    } else {
      formik.setFieldValue("is_part_payment_new", 0);
      formik.setFieldValue("ps_due_amount_new", 0);
      formik.setFieldValue("eps_transaction_id", null);
    }
  }, [
    formik.values.is_deducted_from_wallet,
    offlinePaidData?.ps_amount,
    offlinePaidData?.ps_due_amount,
    studentWalletBalance,
  ]);

  useEffect(() => {
    if (show) {
      fetchStudentWalletBalance();
      // Reset form values when the modal is shown (same for online and offline).
      //formik.resetForm();
      formik.resetForm({
        values: {
          ...formik.initialValues,
          pk_cpm_key_e: "",
        },
      });
    }
  }, [show]);

  const currency = sessionStorage.getItem("currency_code") || "SGD";

  // Whether to show the Payment Mode + Transaction ID fields at all:
  //   OFFLINE (unchanged): remaining balance > 0, OR wallet isn't being used.
  //   ONLINE: only when the wallet is enabled AND still leaves a remaining balance.
  const showPaymentModeSection = isOnlinePayment
    ? Number(formik.values.is_deducted_from_wallet) === 1 && Number(formik.values.ps_due_amount_new) > 0 ? true : studentWalletBalance == 0
    : Number(formik.values.ps_due_amount_new) > 0 || Number(formik.values.is_deducted_from_wallet) !== 1;

  return (
    <Modal show={show} onHide={onHide} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <h5 className="page-title">Payment</h5>
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <Row>
            <Col lg="6" md="6" sm="6" className="position-relative">
              <Card className="rounded-1 mt-2 card-bdr h-100">
                <Card.Body>
                  <div className="cus-pyt-dtls">
                    <h6 className="mb-0">Student Name:</h6>
                    <p className="mb-0">
                      {studentData?.st_first_name
                        ? `${studentData.st_first_name} ${studentData.st_surname || ""}`
                        : "-"}
                    </p>
                  </div>

                  <div className="cus-pyt-dtls">
                    <h6 className="mb-0">Payment Frequency:</h6>
                    <p className="mb-0">{offlinePaidData?.ps_frq_name || "-"}</p>
                  </div>

                  <div className="cus-pyt-dtls">
                    <h6>From Date:</h6>
                    <p className="mb-0">
                      {offlinePaidData?.ps_from_date
                        ? Moment(offlinePaidData.ps_from_date, "YYYY-MM-DD").format("DD-MM-YYYY")
                        : "-"}
                    </p>
                  </div>

                  <div className="cus-pyt-dtls">
                    <h6>To Date:</h6>
                    <p className="mb-0">
                      {offlinePaidData?.ps_end_date
                        ? Moment(offlinePaidData.ps_end_date, "YYYY-MM-DD").format("DD-MM-YYYY")
                        : "-"}
                    </p>
                  </div>

                  <div className="cus-pyt-dtls">
                    <h6>Session Name:</h6>
                    <p className="mb-0">{offlinePaidData?.sest_name || "-"}</p>
                  </div>

                  <div className="cus-pyt-dtls">
                    <h6>Product Name:</h6>
                    <p className="mb-0">{offlinePaidData?.pp_name || "-"}</p>
                  </div>

                  <div className="cus-pyt-dtls">
                    <h6>Total Amount:</h6>
                    <p className="mb-0">
                      {offlinePaidData?.ps_amount || 0} - {currency}
                    </p>
                  </div>

                  {offlinePaidData?.ps_due_amount > 0 && (
                    <div className="cus-pyt-dtls">
                      <h6>Due Amount:</h6>
                      <p className="mb-0">
                        {offlinePaidData.ps_due_amount} - {currency}
                      </p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg="6" md="6" sm="6" className="position-relative">
              <Card className="rounded-1 mt-2 card-bdr h-100">
                <Card.Body>
                  <div className="cus-pyt-dtls mb-2">
                    <h6 className="mb-0">Payment Type:</h6>
                    <p className="mb-0">
                      {offlinePaidData?.pp_pay_method
                        ? parseInt(offlinePaidData.pp_pay_method) === 1
                          ? "Online"
                          : "Offline"
                        : "-"}
                    </p>
                  </div>

                  <div className="mt-3">
                    <Row>
                    {Number(formik.values.is_deducted_from_wallet) === 0 && (
                        <div className="cus-pyt-dtls">
                          <h6>Due Amount:</h6>
                          <p className="mb-0">
                            {Number(offlinePaidData?.is_part_payment) === 1
                              ? Number(offlinePaidData?.ps_due_amount || 0).toFixed(2)
                              : Number(offlinePaidData?.ps_amount || 0).toFixed(2)}{" "}
                            - {currency}
                          </p>
                        </div>
                      )}

                      {Number(studentWalletBalance) > 0 && (
                        <Col
                          lg="12"
                          md="12"
                          sm="12"
                          className="position-relative mt-3 mb-3 d-flex justify-content-between align-items-center"
                        >
                          <h6 className="form-lable required-field">Pay this amount from wallet?</h6>
                          <Form.Check
                            type="switch"
                            id="pk_cpm_key_e_wallet_switch"
                            label={formik.values.is_deducted_from_wallet == 1 ? "Yes" : "No"}
                            checked={formik.values.is_deducted_from_wallet == 1}
                            onChange={(e) =>{
                                formik.setFieldValue("pk_cpm_key_e", null),
                                formik.setFieldValue("is_deducted_from_wallet", e.target.checked ? 1 : 0)}
                            }
                          />
                        </Col>
                      )}

                      {/* {Number(formik.values.is_deducted_from_wallet) === 1 && (
                        <div className="cus-pyt-dtls">
                          <h6>Amount to be Deducted from Wallet:</h6>
                          <p className="mb-0">
                            {Number(offlinePaidData?.is_part_payment) === 1
                              ? (Number(offlinePaidData?.ps_due_amount || 0) - Number(formik.values.ps_due_amount_new || 0)).toFixed(2)
                              : (
                                  Number(offlinePaidData?.ps_amount || 0) -
                                  Number(formik.values.ps_due_amount_new || 0)
                                ).toFixed(2)}{" "}
                            - {currency}
                          </p>
                        </div>
                      )} */}
                      {Number(formik.values.is_deducted_from_wallet) === 1 && (
                        <div className="cus-pyt-dtls">
                            <h6>Amount to be Deducted from Wallet:</h6>
                            <p className="mb-0">
                            {Number(offlinePaidData?.is_part_payment) === 1
                                ? (
                                    Number(offlinePaidData?.ps_due_amount || 0) -
                                    Number(formik.values.ps_due_amount_new || 0)
                                ).toFixed(2)
                                : (
                                    Number(offlinePaidData?.ps_amount || 0) -
                                    Number(formik.values.ps_due_amount_new || 0)
                                ).toFixed(2)}{" "}
                            - {currency}
                            </p>
                        </div>
                        )}

                      {Number(formik.values.ps_due_amount_new) > 0 && (
                        <div className="cus-pyt-dtls">
                          <h6>Due Amount:</h6>
                          <p className="mb-0">
                            {Number(formik.values.ps_due_amount_new).toFixed(2)} - {currency}
                          </p>
                        </div>
                      )}


                       {isOnlinePayment && showPaymentModeSection && (
                        <div className="cus-pyt-dtls">
                          <p className="mb-0 text-muted required-field" style={{ fontSize: "10px" }}>
                            
                              This due amount will be credited to the student&apos;s wallet and applied to this
                              online invoice. {Number(formik.values.ps_due_amount_new) && !formik.values.pk_cpm_key_e ? "This optional not selected payment mode and transaction id will be used for the remaining due of this invoice. only Deducted from Wallet amount will be credited." : ""}
                         
                          </p>
                        </div>
                      )}


                      {showPaymentModeSection && (
                        <>
                          <Col lg="12" md="12" sm="12" className="position-relative mb-2">
                            <h6
                              className={`form-lable ${
                                Number(formik.values.is_deducted_from_wallet) === 1 ? "" : "required-field"
                              }`}
                            >
                              Payment Mode{" "}
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="payment-mode-tooltip" className="custom-tooltip">
                                    The super admin can add, edit, and delete payment mode form.
                                  </Tooltip>
                                }
                              >
                                <Button variant="secondary" className="no-bdr" type="button">
                                  <i className="fa-solid fa-info-circle ml-2 mr-2 mt-1"></i>
                                </Button>
                              </OverlayTrigger>
                            </h6>
                            <Select
                              menuPlacement="auto"
                              options={offlineModesOptions}
                              name="pk_cpm_key_e"
                              value={
                                  offlineModesOptions.find(
                                    (option) =>
                                      option.value?.toString() ===
                                      formik.values.pk_cpm_key_e?.toString()
                                  ) || null
                                }
                              // value={offlineModesOptions.find(
                              //   (option) =>
                              //     option.value &&
                              //     formik.values.pk_cpm_key_e &&
                              //     option.value.toString() === formik.values.pk_cpm_key_e.toString()
                              // )}
                              id="pk_cpm_key_e"
                              onChange={(selectedOption) =>
                                formik.setFieldValue("pk_cpm_key_e", selectedOption["value"])
                              }
                              onBlur={() => formik.setTouched({ ...formik.touched, pk_cpm_key_e: true })}
                              className={
                                formik.errors.pk_cpm_key_e && formik.touched.pk_cpm_key_e ? "input-error" : ""
                              }
                            />
                            {formik.errors.pk_cpm_key_e && formik.touched.pk_cpm_key_e && (
                              <span className="error">{formik.errors.pk_cpm_key_e}</span>
                            )}
                          </Col>

                          {!isWalletOnlyMode(formik.values.pk_cpm_key_e) && (
                            <Col lg="12" md="12" sm="12" className="position-relative">
                              <h6
                                className={`form-lable ${
                                  Number(formik.values.is_deducted_from_wallet) === 1 ? "" : "required-field"
                                }`}
                              >
                                Transaction ID
                              </h6>
                              <Form.Control
                                type="text"
                                id="eps_transaction_id"
                                value={formik.values.eps_transaction_id}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={
                                  formik.errors.eps_transaction_id && formik.touched.eps_transaction_id
                                    ? "input-error"
                                    : "mb-0"
                                }
                              />
                              {formik.errors.eps_transaction_id && formik.touched.eps_transaction_id && (
                                <p className="error">{formik.errors.eps_transaction_id}</p>
                              )}
                            </Col>
                          )}
                        </>
                      )}
                    </Row>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="pt-0">
          <div className="mx-auto d-flex gap-2">
            <Button variant="contained" className="primary-gray" onClick={onHide} type="button">
              Cancel
            </Button>
            <Button variant="contained" type="submit" className="primary-filled" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default OfflineSetAsPaidPopup;